import React, { useState } from 'react';
import {
  AlertCircle,
  AlertTriangle,
  CheckCircle2,
  DollarSign,
  Users,
  Sliders,
  Download,
  Upload,
  Calendar,
  Layers,
  Edit3,
  UserCheck,
  ShieldAlert,
  Info,
  Clock,
  BarChart3,
  Sun,
  Sunrise,
  Moon,
} from 'lucide-react';
import { calculateFinancials } from '../../core/financials.ts';
import { ALL_RULES, DEFAULT_CATEGORY_MAX_RATIOS } from '../../core/linter.ts';
import { calculateTimeSlotAnalysis, PRIME_TIME_SLOTS } from '../../core/timeSlots.ts';
import { Category, ConstraintViolation, EventItem, RuleConfig, TimeSlot } from '../../core/types.ts';
import { OrganisersPerDayGraph } from '../components/OrganisersPerDayGraph.tsx';
import { parseUrlState, updateUrlState } from '../../services/urlState.ts';

interface AnalysisPageProps {
  events: EventItem[];
  violations: ConstraintViolation[];
  ruleConfig: RuleConfig;
  onUpdateRuleConfig: (newConfig: RuleConfig) => void;
  onEditEvent: (event: EventItem) => void;
  onExportJSON: () => void;
  onImportClick: () => void;
  defaultSubTab?: AnalyticsTabId;
}

const CATEGORIES: Category[] = [
  'Drinking',
  'Trip',
  'Sports',
  'Social',
  'Culture',
  'Art',
  'Academic',
  'Other',
];

export type AnalyticsTabId = 'ratio-analytics' | 'org-graph' | 'slot-analytics' | 'linting' | 'config' | 'data' | 'all';

export const AnalysisPage: React.FC<AnalysisPageProps> = ({
  events,
  violations,
  ruleConfig,
  onUpdateRuleConfig,
  onEditEvent,
  onExportJSON,
  onImportClick,
}) => {
  const initialSubtab = parseUrlState().subtab as AnalyticsTabId;
  const [activeTab, setActiveTab] = useState<AnalyticsTabId>(
    initialSubtab && ['ratio-analytics', 'org-graph', 'slot-analytics', 'linting', 'config', 'data', 'all'].includes(initialSubtab)
      ? initialSubtab
      : 'ratio-analytics'
  );

  const handleSubtabClick = (tabId: AnalyticsTabId) => {
    setActiveTab(tabId);
    updateUrlState({ subtab: tabId });
  };

  const financials = calculateFinancials(events, ruleConfig);
  const timeSlotAnalysis = calculateTimeSlotAnalysis(events);

  const hardViolations = violations.filter(v => v.severity === 'hard');
  const softViolations = violations.filter(v => v.severity === 'soft');

  const ratioViolations = violations.filter(v => v.ruleId === 'participant-organiser-ratio');
  const hasExceededRatios = Object.values(financials.categoryRatios).some(r => r.isExceeded) || ratioViolations.length > 0;

  const slotViolations = violations.filter(v => v.ruleId === 'prime-time-coverage');

  const handleConfigChange = (key: keyof RuleConfig, value: any) => {
    onUpdateRuleConfig({
      ...ruleConfig,
      [key]: value,
    });
  };

  const handleCategoryRatioChange = (cat: Category, value: number) => {
    onUpdateRuleConfig({
      ...ruleConfig,
      categoryMaxRatioThresholds: {
        ...DEFAULT_CATEGORY_MAX_RATIOS,
        ...(ruleConfig.categoryMaxRatioThresholds || {}),
        [cat]: value,
      },
    });
  };

  const isTabVisible = (tabId: AnalyticsTabId) => activeTab === 'all' || activeTab === tabId;

  return (
    <div className="main-container analysis-page">
      {/* Top Stat Ribbon */}
      <div className="stat-grid">
        <div className="stat-card">
          <div className="stat-icon accent-indigo">
            <Calendar size={24} strokeWidth={1.8} />
          </div>
          <div className="stat-content">
            <span className="stat-label">Total Events</span>
            <span className="stat-value">{financials.totalEvents}</span>
          </div>
        </div>

        <div className="stat-card">
          <div className="stat-icon accent-indigo">
            <Users size={24} strokeWidth={1.8} />
          </div>
          <div className="stat-content">
            <span className="stat-label">Total Spots Across Events</span>
            <span className="stat-value">{financials.totalSpots.toLocaleString()} spots</span>
          </div>
        </div>

        <div className="stat-card">
          <div className={`stat-icon ${hasExceededRatios ? 'accent-amber' : 'accent-emerald'}`}>
            <UserCheck size={24} strokeWidth={1.8} />
          </div>
          <div className="stat-content">
            <span className="stat-label">Avg Organiser Ratio</span>
            <span className={`stat-value ${hasExceededRatios ? 'text-amber' : 'text-emerald'}`}>
              {financials.overallRatio.toFixed(1)} <span style={{ fontSize: '0.85rem', fontWeight: 600 }}>spots/org</span>
            </span>
          </div>
        </div>

        <div className="stat-card">
          <div className={`stat-icon ${timeSlotAnalysis.primeTimeCoverageScore >= 80 ? 'accent-emerald' : 'accent-amber'}`}>
            <Clock size={24} strokeWidth={1.8} />
          </div>
          <div className="stat-content">
            <span className="stat-label">Prime-Time Fill Index</span>
            <span className={`stat-value ${timeSlotAnalysis.primeTimeCoverageScore >= 80 ? 'text-emerald' : 'text-amber'}`}>
              {timeSlotAnalysis.primeTimeCoverageScore.toFixed(0)}%
            </span>
          </div>
        </div>

        <div className="stat-card">
          <div className={`stat-icon ${hardViolations.length > 0 ? 'accent-red' : 'accent-emerald'}`}>
            {hardViolations.length > 0 ? <AlertCircle size={24} strokeWidth={1.8} /> : <CheckCircle2 size={24} strokeWidth={1.8} />}
          </div>
          <div className="stat-content">
            <span className="stat-label">Hard Constraints</span>
            <span className={`stat-value ${hardViolations.length > 0 ? 'text-red' : 'text-emerald'}`}>
              {hardViolations.length > 0 ? `${hardViolations.length} Violations` : 'Passed Clean'}
            </span>
          </div>
        </div>

        <div className="stat-card">
          <div className={`stat-icon ${softViolations.length > 0 ? 'accent-amber' : 'accent-emerald'}`}>
            <AlertTriangle size={24} strokeWidth={1.8} />
          </div>
          <div className="stat-content">
            <span className="stat-label">Soft Warnings</span>
            <span className={`stat-value ${softViolations.length > 0 ? 'text-amber' : 'text-emerald'}`}>
              {softViolations.length} Warning{softViolations.length !== 1 ? 's' : ''}
            </span>
          </div>
        </div>
      </div>

      {/* Analytics Sub-Tabs Navigation */}
      <div className="analytics-tabs-container">
        <div className="analytics-tabs">
          <button
            className={`analytics-tab ${activeTab === 'ratio-analytics' ? 'active' : ''}`}
            onClick={() => handleSubtabClick('ratio-analytics')}
          >
            <UserCheck size={18} />
            <span>Organiser Ratios</span>
            {hasExceededRatios && <span className="nav-badge soft">Warning</span>}
          </button>

          <button
            className={`analytics-tab ${activeTab === 'org-graph' ? 'active' : ''}`}
            onClick={() => handleSubtabClick('org-graph')}
          >
            <BarChart3 size={18} />
            <span>Organisers per Day</span>
          </button>

          <button
            className={`analytics-tab ${activeTab === 'slot-analytics' ? 'active' : ''}`}
            onClick={() => handleSubtabClick('slot-analytics')}
          >
            <Clock size={18} />
            <span>Time Slot Fill &amp; Graph</span>
            {slotViolations.length > 0 && <span className="nav-badge soft">{slotViolations.length} Gaps</span>}
          </button>

          <button
            className={`analytics-tab ${activeTab === 'linting' ? 'active' : ''}`}
            onClick={() => handleSubtabClick('linting')}
          >
            <AlertCircle size={18} />
            <span>Constraint Linting</span>
            {violations.length > 0 && (
              <span className={`nav-badge ${hardViolations.length > 0 ? 'hard' : 'soft'}`}>
                {violations.length}
              </span>
            )}
          </button>

          <button
            className={`analytics-tab ${activeTab === 'config' ? 'active' : ''}`}
            onClick={() => handleSubtabClick('config')}
          >
            <Sliders size={18} />
            <span>Framework Config</span>
          </button>

          <button
            className={`analytics-tab ${activeTab === 'data' ? 'active' : ''}`}
            onClick={() => handleSubtabClick('data')}
          >
            <Layers size={18} />
            <span>Data Management</span>
          </button>

          <button
            className={`analytics-tab ${activeTab === 'all' ? 'active' : ''}`}
            onClick={() => handleSubtabClick('all')}
          >
            <span>All Sections</span>
          </button>
        </div>
      </div>

      {/* Tab 1: Organiser Staffing & Participant-to-Organiser Ratio Analytics */}
      {isTabVisible('ratio-analytics') && (
        <div className="analysis-section">
          <div className="section-header">
            <h2 className="section-title">
              <UserCheck size={20} color="var(--accent-primary)" />
              <span>Organiser Ratio &amp; Capacity Analytics</span>
            </h2>
            <span style={{ fontSize: '0.85rem', color: 'var(--text-muted)' }}>
              Total Spots: {financials.totalSpots.toLocaleString()} across {financials.totalEvents} events | {financials.totalOrganisersAssigned} Organisers assigned
            </span>
          </div>

          <div style={{ background: 'rgba(255, 255, 255, 0.03)', border: '1px solid var(--border)', borderRadius: 'var(--radius-md)', padding: '1rem 1.25rem', marginBottom: '1.5rem', display: 'flex', alignItems: 'center', gap: '0.85rem' }}>
            <Info size={22} color="var(--accent-primary)" style={{ flexShrink: 0 }} />
            <p style={{ fontSize: '0.85rem', color: 'var(--text-secondary)' }}>
              <strong>Staffing Threshold Logic:</strong> Certain event categories require tighter supervision (e.g. <em>Trip</em> &amp; <em>Drinking</em> require max 10 spots/org, <em>Sports</em> max 15), while lower-risk or structured categories require fewer organisers per participant (e.g. <em>Academic</em> max 30 spots/org, <em>Culture</em> &amp; <em>Art</em> max 25).
            </p>
          </div>

          {/* Category Ratio Cards Grid */}
          <div className="category-ratio-grid">
            {CATEGORIES.map(cat => {
              const info = financials.categoryRatios[cat];
              const eventCount = financials.categoryBreakdown[cat] || 0;
              if (eventCount === 0) return null;

              const percent = info.organisers > 0 ? Math.min((info.ratio / info.maxThreshold) * 100, 100) : 100;
              const isWarning = info.isExceeded || (info.spots > 0 && info.organisers === 0);

              return (
                <div key={cat} className={`category-ratio-card ${isWarning ? 'exceeded' : ''}`}>
                  <div className="ratio-header">
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                      <span className="cat-pill active" style={{ padding: '0.2rem 0.6rem', fontSize: '0.75rem', borderLeft: `3px solid var(--cat-${cat.toLowerCase()})` }}>
                        {cat}
                      </span>
                      <span style={{ fontSize: '0.75rem', color: 'var(--text-muted)' }}>({eventCount} event{eventCount > 1 ? 's' : ''})</span>
                    </div>
                    <span className={`lint-indicator ${isWarning ? 'soft' : 'hard'}`} style={{ fontSize: '0.7rem', padding: '0.15rem 0.5rem', background: isWarning ? 'rgba(245, 158, 11, 0.2)' : 'rgba(16, 185, 129, 0.2)', color: isWarning ? '#fde047' : '#34d399', border: 'none' }}>
                      {isWarning ? 'HIGH RATIO' : 'OK'}
                    </span>
                  </div>

                  <div className="ratio-metrics">
                    <div>
                      <span className="ratio-value" style={{ color: isWarning ? '#fde047' : 'var(--text-primary)' }}>
                        {info.organisers > 0 ? info.ratio.toFixed(1) : '∞'}
                      </span>
                      <span style={{ fontSize: '0.8rem', color: 'var(--text-muted)', marginLeft: '0.35rem' }}>spots/org</span>
                    </div>
                    <div style={{ textAlign: 'right' }}>
                      <span style={{ fontSize: '0.75rem', color: 'var(--text-muted)' }}>Max Threshold:</span>
                      <div style={{ fontSize: '0.875rem', fontWeight: 700, color: 'var(--text-primary)' }}>
                        ≤ {info.maxThreshold} spots/org
                      </div>
                    </div>
                  </div>

                  <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.75rem', color: 'var(--text-dim)', marginTop: '0.1rem' }}>
                    <span>{info.spots} spots total</span>
                    <span>{info.organisers} organisers</span>
                  </div>

                  <div className="ratio-bar-track">
                    <div
                      className="ratio-bar-fill"
                      style={{
                        width: `${percent}%`,
                        backgroundColor: isWarning ? '#f59e0b' : info.ratio > info.maxThreshold * 0.8 ? '#fbbf24' : '#10b981',
                      }}
                    />
                  </div>
                </div>
              );
            })}
          </div>

          {/* Required Organisers per Day Graph */}
          <OrganisersPerDayGraph
            events={events}
            maxOrganisersThreshold={ruleConfig.maxOrganisersPerTimeSlot || 10}
            onEditEvent={onEditEvent}
          />

          {/* Detailed Event Ratio Inspection Table */}
          <div style={{ marginTop: '2rem' }}>
            <h3 style={{ fontSize: '1rem', fontWeight: 700, marginBottom: '0.85rem', color: 'var(--text-primary)' }}>
              Event-Level Organiser Ratio Inspection
            </h3>
            <div style={{ overflowX: 'auto' }}>
              <table className="data-table">
                <thead>
                  <tr>
                    <th>Event Title</th>
                    <th>Category</th>
                    <th>Total Spots</th>
                    <th>Organisers</th>
                    <th>Event Ratio</th>
                    <th>Category Threshold</th>
                    <th>Status</th>
                    <th>Action</th>
                  </tr>
                </thead>
                <tbody>
                  {events
                    .map(ev => {
                      const capacity = Number(ev.capacity) || 0;
                      const orgs = Number(ev.organisers) || 0;
                      const cat = ev.category || 'Other';
                      const catThreshold = ruleConfig.categoryMaxRatioThresholds?.[cat]
                        ?? DEFAULT_CATEGORY_MAX_RATIOS[cat]
                        ?? ruleConfig.maxParticipantsPerOrganiserDefault
                        ?? 20;
                      const ratio = orgs > 0 ? capacity / orgs : capacity > 0 ? Infinity : 0;
                      const isExceeded = ratio > catThreshold || (capacity > 0 && orgs === 0);

                      return { ev, capacity, orgs, cat, catThreshold, ratio, isExceeded };
                    })
                    .sort((a, b) => (b.isExceeded ? 1 : 0) - (a.isExceeded ? 1 : 0) || b.ratio - a.ratio)
                    .map(({ ev, capacity, orgs, cat, catThreshold, ratio, isExceeded }) => (
                      <tr key={ev.id} style={{ background: isExceeded ? 'rgba(245, 158, 11, 0.04)' : undefined }}>
                        <td>
                          <strong style={{ color: 'var(--text-primary)' }}>{ev.title}</strong>
                        </td>
                        <td>
                          <span className="cat-pill active" style={{ padding: '0.15rem 0.5rem', fontSize: '0.75rem', borderLeft: `3px solid var(--cat-${cat.toLowerCase()})` }}>
                            {cat}
                          </span>
                        </td>
                        <td>{capacity} spots</td>
                        <td>{orgs} orgs</td>
                        <td style={{ fontWeight: 700, color: isExceeded ? '#f59e0b' : 'inherit' }}>
                          {orgs > 0 ? `${ratio.toFixed(1)} spots/org` : '0 orgs (Unstaffed)'}
                        </td>
                        <td>≤ {catThreshold} spots/org</td>
                        <td>
                          {isExceeded ? (
                            <span className="nav-badge soft" style={{ display: 'inline-flex', alignItems: 'center', gap: '0.3rem' }}>
                              <ShieldAlert size={12} /> Exceeds Threshold
                            </span>
                          ) : (
                            <span className="brand-badge" style={{ background: '#10b981', color: '#ffffff' }}>
                              Optimal
                            </span>
                          )}
                        </td>
                        <td>
                          <button className="btn btn-secondary btn-sm" style={{ padding: '0.2rem 0.6rem', fontSize: '0.75rem' }} onClick={() => onEditEvent(ev)}>
                            <Edit3 size={12} /> Edit
                          </button>
                        </td>
                      </tr>
                    ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {/* Standalone Tab: Organisers per Day Graph */}
      {activeTab === 'org-graph' && (
        <div className="analysis-section">
          <div className="section-header">
            <h2 className="section-title">
              <BarChart3 size={20} color="var(--accent-primary)" />
              <span>Required Organisers per Day Analysis</span>
            </h2>
            <span style={{ fontSize: '0.85rem', color: 'var(--text-muted)' }}>
              Detailed daily staffing demand graph across O-Weeks
            </span>
          </div>

          <OrganisersPerDayGraph
            events={events}
            maxOrganisersThreshold={ruleConfig.maxOrganisersPerTimeSlot || 10}
            onEditEvent={onEditEvent}
          />
        </div>
      )}

      {/* Tab 2: Time Slot Fill & Distribution Graph */}
      {isTabVisible('slot-analytics') && (
        <div className="analysis-section">
          <div className="section-header">
            <h2 className="section-title">
              <Clock size={20} color="var(--accent-primary)" />
              <span>Time Slot Fill &amp; Daily Distribution Graph</span>
            </h2>
            <span style={{ fontSize: '0.85rem', color: 'var(--text-muted)' }}>
              Prime-Time Fill Score: <strong style={{ color: timeSlotAnalysis.primeTimeCoverageScore >= 80 ? '#10b981' : '#f59e0b' }}>{timeSlotAnalysis.primeTimeCoverageScore.toFixed(1)}%</strong> ({timeSlotAnalysis.totalPrimeSlotsFilled} / {timeSlotAnalysis.totalPrimeSlotsAvailable} prime slots filled)
            </span>
          </div>

          <div style={{ background: 'rgba(255, 255, 255, 0.03)', border: '1px solid var(--border)', borderRadius: 'var(--radius-md)', padding: '1rem 1.25rem', marginBottom: '1.5rem', display: 'flex', alignItems: 'center', gap: '0.85rem' }}>
            <Info size={22} color="var(--accent-primary)" style={{ flexShrink: 0 }} />
            <p style={{ fontSize: '0.85rem', color: 'var(--text-secondary)' }}>
              <strong>Prime-Time Coverage Metric:</strong> Evaluates slot coverage across <strong>Noon</strong> (12:00-14:30), <strong>Afternoon</strong> (14:30-18:00), and <strong>Evening</strong> (18:00+) to ensure continuous student activity without dead periods. Morning events (05:00-12:00) are displayed in the graph but ignored for this fill optimization score.
            </p>
          </div>

          {/* Time Slot Totals Overview Ribbon */}
          <div className="rule-config-grid" style={{ marginBottom: '2rem' }}>
            <div className="config-card" style={{ borderLeft: '4px solid #38bdf8' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <span className="slot-pill-morning" style={{ padding: '0.2rem 0.6rem', fontSize: '0.75rem', borderRadius: 'var(--radius-full)', fontWeight: 700 }}>
                  <Sunrise size={12} style={{ display: 'inline', marginRight: '4px' }} /> Morning (05:00 - 12:00)
                </span>
                <span style={{ fontSize: '0.75rem', color: 'var(--text-muted)' }}>Ignored in Score</span>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginTop: '0.5rem' }}>
                <span style={{ fontSize: '1.5rem', fontWeight: 800 }}>{timeSlotAnalysis.slotTotals.Morning.count} events</span>
                <span style={{ fontSize: '0.85rem', color: 'var(--text-dim)' }}>{timeSlotAnalysis.slotTotals.Morning.spots} spots</span>
              </div>
            </div>

            <div className="config-card" style={{ borderLeft: '4px solid #fbbf24' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <span className="slot-pill-noon" style={{ padding: '0.2rem 0.6rem', fontSize: '0.75rem', borderRadius: 'var(--radius-full)', fontWeight: 700 }}>
                  <Sun size={12} style={{ display: 'inline', marginRight: '4px' }} /> Noon (12:00 - 14:30)
                </span>
                <span className="brand-badge" style={{ background: '#f59e0b' }}>Prime Slot</span>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginTop: '0.5rem' }}>
                <span style={{ fontSize: '1.5rem', fontWeight: 800 }}>{timeSlotAnalysis.slotTotals.Noon.count} events</span>
                <span style={{ fontSize: '0.85rem', color: 'var(--text-dim)' }}>{timeSlotAnalysis.slotTotals.Noon.spots} spots</span>
              </div>
            </div>

            <div className="config-card" style={{ borderLeft: '4px solid #34d399' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <span className="slot-pill-afternoon" style={{ padding: '0.2rem 0.6rem', fontSize: '0.75rem', borderRadius: 'var(--radius-full)', fontWeight: 700 }}>
                  <Sun size={12} style={{ display: 'inline', marginRight: '4px' }} /> Afternoon (14:30 - 18:00)
                </span>
                <span className="brand-badge" style={{ background: '#10b981' }}>Prime Slot</span>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginTop: '0.5rem' }}>
                <span style={{ fontSize: '1.5rem', fontWeight: 800 }}>{timeSlotAnalysis.slotTotals.Afternoon.count} events</span>
                <span style={{ fontSize: '0.85rem', color: 'var(--text-dim)' }}>{timeSlotAnalysis.slotTotals.Afternoon.spots} spots</span>
              </div>
            </div>

            <div className="config-card" style={{ borderLeft: '4px solid #c084fc' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <span className="slot-pill-evening" style={{ padding: '0.2rem 0.6rem', fontSize: '0.75rem', borderRadius: 'var(--radius-full)', fontWeight: 700 }}>
                  <Moon size={12} style={{ display: 'inline', marginRight: '4px' }} /> Evening (18:00+)
                </span>
                <span className="brand-badge" style={{ background: '#8b5cf6' }}>Prime Slot</span>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginTop: '0.5rem' }}>
                <span style={{ fontSize: '1.5rem', fontWeight: 800 }}>{timeSlotAnalysis.slotTotals.Evening.count} events</span>
                <span style={{ fontSize: '0.85rem', color: 'var(--text-dim)' }}>{timeSlotAnalysis.slotTotals.Evening.spots} spots</span>
              </div>
            </div>
          </div>

          {/* Daily Distribution Graph Grid */}
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.75rem' }}>
            <h3 style={{ fontSize: '1.05rem', fontWeight: 700, color: 'var(--text-primary)' }}>
              Daily Time-Slot Fill Graph (Sept 28 – Oct 11)
            </h3>
            <div style={{ display: 'flex', gap: '1rem', fontSize: '0.75rem', color: 'var(--text-muted)' }}>
              <span style={{ display: 'inline-flex', alignItems: 'center', gap: '0.3rem' }}><span style={{ width: 10, height: 10, borderRadius: 2, background: '#38bdf8' }}></span> Morning</span>
              <span style={{ display: 'inline-flex', alignItems: 'center', gap: '0.3rem' }}><span style={{ width: 10, height: 10, borderRadius: 2, background: '#fbbf24' }}></span> Noon</span>
              <span style={{ display: 'inline-flex', alignItems: 'center', gap: '0.3rem' }}><span style={{ width: 10, height: 10, borderRadius: 2, background: '#34d399' }}></span> Afternoon</span>
              <span style={{ display: 'inline-flex', alignItems: 'center', gap: '0.3rem' }}><span style={{ width: 10, height: 10, borderRadius: 2, background: '#c084fc' }}></span> Evening</span>
            </div>
          </div>

          <div className="daily-graph-grid">
            {timeSlotAnalysis.daysSummary.map(day => {
              const totalDayEvents = Object.values(day.slots).reduce((acc, s) => acc + s.count, 0);

              const morningPct = totalDayEvents > 0 ? (day.slots.Morning.count / totalDayEvents) * 100 : 0;
              const noonPct = totalDayEvents > 0 ? (day.slots.Noon.count / totalDayEvents) * 100 : 0;
              const afternoonPct = totalDayEvents > 0 ? (day.slots.Afternoon.count / totalDayEvents) * 100 : 0;
              const eveningPct = totalDayEvents > 0 ? (day.slots.Evening.count / totalDayEvents) * 100 : 0;

              const isFullyCovered = day.filledPrimeSlotsCount === 3;

              return (
                <div key={day.dateStr} className={`daily-graph-card ${isFullyCovered ? 'full-coverage' : 'partial-coverage'}`}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <div>
                      <strong style={{ fontSize: '0.95rem', color: 'var(--text-primary)' }}>{day.formattedDate}</strong>
                      <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)' }}>{day.dateStr}</div>
                    </div>
                    <span className={`lint-indicator ${isFullyCovered ? 'hard' : 'soft'}`} style={{ fontSize: '0.7rem', padding: '0.15rem 0.5rem', background: isFullyCovered ? 'rgba(16, 185, 129, 0.2)' : 'rgba(245, 158, 11, 0.2)', color: isFullyCovered ? '#34d399' : '#fde047', border: 'none' }}>
                      {day.filledPrimeSlotsCount}/3 PRIME SLOTS
                    </span>
                  </div>

                  {/* Stacked Distribution Bar */}
                  <div>
                    <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.75rem', color: 'var(--text-dim)', marginBottom: '0.35rem' }}>
                      <span>{totalDayEvents} event{totalDayEvents !== 1 ? 's' : ''} scheduled</span>
                      <span>{day.primeCoveragePercent.toFixed(0)}% prime fill</span>
                    </div>
                    <div className="slot-stacked-bar">
                      <div className="slot-bar-segment morning" style={{ width: `${morningPct}%` }} title={`Morning: ${day.slots.Morning.count} events`} />
                      <div className="slot-bar-segment noon" style={{ width: `${noonPct}%` }} title={`Noon: ${day.slots.Noon.count} events`} />
                      <div className="slot-bar-segment afternoon" style={{ width: `${afternoonPct}%` }} title={`Afternoon: ${day.slots.Afternoon.count} events`} />
                      <div className="slot-bar-segment evening" style={{ width: `${eveningPct}%` }} title={`Evening: ${day.slots.Evening.count} events`} />
                    </div>
                  </div>

                  {/* Slot Pills Breakdown */}
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.35rem', marginTop: '0.2rem' }}>
                    {day.slots.Morning.count > 0 && (
                      <span className="slot-pill-morning">
                        Morning ({day.slots.Morning.count})
                      </span>
                    )}
                    <span className={day.slots.Noon.count > 0 ? 'slot-pill-noon' : 'nav-badge soft'} style={{ opacity: day.slots.Noon.count > 0 ? 1 : 0.6 }}>
                      Noon {day.slots.Noon.count > 0 ? `(${day.slots.Noon.count})` : '✗'}
                    </span>
                    <span className={day.slots.Afternoon.count > 0 ? 'slot-pill-afternoon' : 'nav-badge soft'} style={{ opacity: day.slots.Afternoon.count > 0 ? 1 : 0.6 }}>
                      Afternoon {day.slots.Afternoon.count > 0 ? `(${day.slots.Afternoon.count})` : '✗'}
                    </span>
                    <span className={day.slots.Evening.count > 0 ? 'slot-pill-evening' : 'nav-badge soft'} style={{ opacity: day.slots.Evening.count > 0 ? 1 : 0.6 }}>
                      Evening {day.slots.Evening.count > 0 ? `(${day.slots.Evening.count})` : '✗'}
                    </span>
                  </div>

                  {/* Detailed Event List for Day */}
                  {totalDayEvents > 0 && (
                    <div style={{ marginTop: '0.3rem', borderTop: '1px solid var(--border-subtle)', paddingTop: '0.5rem' }}>
                      {Object.entries(day.slots).map(([slotName, sInfo]) => {
                        if (sInfo.events.length === 0) return null;
                        return (
                          <div key={slotName} style={{ marginBottom: '0.25rem', fontSize: '0.75rem' }}>
                            <span style={{ fontWeight: 600, color: 'var(--text-muted)', display: 'inline-block', minWidth: '70px' }}>{slotName}:</span>
                            <span style={{ color: 'var(--text-secondary)' }}>
                              {sInfo.events.map(e => e.title).join(', ')}
                            </span>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Tab 3: Linting Results */}
      {isTabVisible('linting') && (
        <div className="analysis-section">
          <div className="section-header">
            <h2 className="section-title">
              <AlertCircle size={20} color="var(--accent-primary)" />
              <span>Constraint Linting &amp; Schedule Checks</span>
            </h2>
            <span style={{ fontSize: '0.85rem', color: 'var(--text-muted)' }}>
              Real-time automated audit across {ALL_RULES.length} active rules
            </span>
          </div>

          {violations.length === 0 ? (
            <div
              style={{
                padding: '2rem',
                textAlign: 'center',
                background: 'rgba(16, 185, 129, 0.05)',
                border: '1px solid rgba(16, 185, 129, 0.2)',
                borderRadius: 'var(--radius-lg)',
                color: '#10b981',
              }}
            >
              <CheckCircle2 size={36} style={{ marginBottom: '0.5rem' }} />
              <h3 style={{ fontSize: '1.1rem', fontWeight: 700 }}>Schedule Fully Compliant!</h3>
              <p style={{ fontSize: '0.875rem', color: 'var(--text-muted)', marginTop: '0.25rem' }}>
                No hard constraint violations or soft warnings detected across the orientation weeks.
              </p>
            </div>
          ) : (
            <div className="violation-list">
              {violations.map((viol, index) => {
                const affectedEvents = events.filter(e => viol.affectedEventIds.includes(e.id));
                return (
                  <div
                    key={`${viol.ruleId}-${index}`}
                    className={`violation-card ${viol.severity}`}
                  >
                    <div className="violation-header">
                      <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                        <span className={`lint-indicator ${viol.severity}`}>
                          {viol.severity.toUpperCase()}
                        </span>
                        <span className="violation-title">{viol.ruleName}</span>
                      </div>
                    </div>

                    <p className="violation-msg">{viol.message}</p>
                    {viol.details && <p className="violation-details">{viol.details}</p>}

                    {affectedEvents.length > 0 && (
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.4rem', marginTop: '0.4rem' }}>
                        <span style={{ fontSize: '0.75rem', color: 'var(--text-dim)' }}>Affected Events:</span>
                        {affectedEvents.map(ev => (
                          <button
                            key={ev.id}
                            className="btn btn-secondary btn-sm"
                            style={{ padding: '0.15rem 0.5rem', fontSize: '0.75rem' }}
                            onClick={() => onEditEvent(ev)}
                          >
                            <Edit3 size={12} />
                            <span>{ev.title}</span>
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      {/* Tab 4: Rule Configurator */}
      {isTabVisible('config') && (
        <div className="analysis-section">
          <div className="section-header">
            <h2 className="section-title">
              <Sliders size={20} color="var(--accent-primary)" />
              <span>Constraint Framework Configuration</span>
            </h2>
          </div>

          <div className="rule-config-grid">
            <div className="config-card">
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <strong style={{ fontSize: '0.9rem', color: 'var(--text-primary)' }}>Max Concurrent Events Limit (Hard)</strong>
                <span className="brand-badge" style={{ background: '#ef4444' }}>Hard Rule</span>
              </div>
              <p style={{ fontSize: '0.8rem', color: 'var(--text-muted)' }}>
                Flags an error if more than N events take place simultaneously in any time slot.
              </p>
              <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
                <input
                  type="range"
                  min="1"
                  max="5"
                  value={ruleConfig.maxConcurrentEvents}
                  onChange={e => handleConfigChange('maxConcurrentEvents', parseInt(e.target.value, 10))}
                  style={{ flex: 1 }}
                />
                <span style={{ fontWeight: 700, minWidth: '40px' }}>{ruleConfig.maxConcurrentEvents} max</span>
              </div>
            </div>

            <div className="config-card">
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <strong style={{ fontSize: '0.9rem', color: 'var(--text-primary)' }}>Min Culture Events per Week (Soft)</strong>
                <span className="brand-badge" style={{ background: '#f59e0b' }}>Global Soft</span>
              </div>
              <p style={{ fontSize: '0.8rem', color: 'var(--text-muted)' }}>
                Ensures at least N Culture events are scheduled in Week 1 and Week 2.
              </p>
              <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
                <input
                  type="range"
                  min="1"
                  max="5"
                  value={ruleConfig.minCultureEventsPerWeek}
                  onChange={e => handleConfigChange('minCultureEventsPerWeek', parseInt(e.target.value, 10))}
                  style={{ flex: 1 }}
                />
                <span style={{ fontWeight: 700, minWidth: '40px' }}>{ruleConfig.minCultureEventsPerWeek} min</span>
              </div>
            </div>

            <div className="config-card">
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <strong style={{ fontSize: '0.9rem', color: 'var(--text-primary)' }}>Max Concurrent Organisers (Soft)</strong>
                <span className="brand-badge" style={{ background: '#f59e0b' }}>Capacity</span>
              </div>
              <p style={{ fontSize: '0.8rem', color: 'var(--text-muted)' }}>
                Warns if required staff across simultaneous events exceeds threshold.
              </p>
              <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
                <input
                  type="range"
                  min="5"
                  max="25"
                  value={ruleConfig.maxOrganisersPerTimeSlot}
                  onChange={e => handleConfigChange('maxOrganisersPerTimeSlot', parseInt(e.target.value, 10))}
                  style={{ flex: 1 }}
                />
                <span style={{ fontWeight: 700, minWidth: '40px' }}>{ruleConfig.maxOrganisersPerTimeSlot} orgs</span>
              </div>
            </div>

            <div className="config-card">
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <strong style={{ fontSize: '0.9rem', color: 'var(--text-primary)' }}>Warn on Budget Deficits (Soft)</strong>
                <span className="brand-badge" style={{ background: '#f59e0b' }}>Financial</span>
              </div>
              <p style={{ fontSize: '0.8rem', color: 'var(--text-muted)' }}>
                Triggers warning when an event total cost exceeds max ticket revenue.
              </p>
              <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer', marginTop: '0.25rem' }}>
                <input
                  type="checkbox"
                  checked={ruleConfig.warnOnDeficit}
                  onChange={e => handleConfigChange('warnOnDeficit', e.target.checked)}
                />
                <span style={{ fontSize: '0.875rem' }}>Enable budget deficit check</span>
              </label>
            </div>
          </div>

          {/* Category Ratio Threshold Controls */}
          <div style={{ marginTop: '1.5rem', paddingTop: '1.5rem', borderTop: '1px solid var(--border)' }}>
            <h3 style={{ fontSize: '1rem', fontWeight: 700, marginBottom: '0.5rem', color: 'var(--text-primary)' }}>
              Category Participant-to-Organiser Thresholds
            </h3>
            <p style={{ fontSize: '0.8rem', color: 'var(--text-muted)', marginBottom: '1.25rem' }}>
              Customize maximum spots allowed per assigned organiser for each event category. Lower thresholds enforce tighter staffing supervision.
            </p>

            <div className="rule-config-grid">
              {CATEGORIES.map(cat => {
                const currentThreshold = ruleConfig.categoryMaxRatioThresholds?.[cat]
                  ?? DEFAULT_CATEGORY_MAX_RATIOS[cat]
                  ?? ruleConfig.maxParticipantsPerOrganiserDefault
                  ?? 20;

                return (
                  <div key={cat} className="config-card" style={{ padding: '1.15rem' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <span className="cat-pill active" style={{ padding: '0.15rem 0.5rem', fontSize: '0.75rem', borderLeft: `3px solid var(--cat-${cat.toLowerCase()})` }}>
                        {cat} Threshold
                      </span>
                      <span style={{ fontSize: '0.8rem', fontWeight: 700, color: 'var(--accent-primary)' }}>
                        ≤ {currentThreshold} spots/org
                      </span>
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginTop: '0.5rem' }}>
                      <input
                        type="range"
                        min="5"
                        max="50"
                        step="1"
                        value={currentThreshold}
                        onChange={e => handleCategoryRatioChange(cat, parseInt(e.target.value, 10))}
                        style={{ flex: 1 }}
                      />
                      <span style={{ fontSize: '0.8rem', fontWeight: 600, minWidth: '35px', textAlign: 'right' }}>
                        {currentThreshold}
                      </span>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      )}

      {/* Tab 6: Data Management */}
      {isTabVisible('data') && (
        <div className="analysis-section">
          <div className="section-header">
            <h2 className="section-title">
              <Layers size={20} color="var(--accent-primary)" />
              <span>State &amp; Data Management</span>
            </h2>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1.5rem' }}>
            <div className="json-dropzone" onClick={onExportJSON}>
              <Download size={32} color="var(--accent-primary)" style={{ marginBottom: '0.75rem' }} />
              <h3 style={{ fontSize: '1rem', fontWeight: 700 }}>Export Schedule to JSON</h3>
              <p style={{ fontSize: '0.8rem', color: 'var(--text-muted)', marginTop: '0.25rem' }}>
                Download complete state (events, pricing, capacity, dates) as formatted JSON file.
              </p>
            </div>

            <div className="json-dropzone" onClick={onImportClick}>
              <Upload size={32} color="var(--accent-primary)" style={{ marginBottom: '0.75rem' }} />
              <h3 style={{ fontSize: '1rem', fontWeight: 700 }}>Import Schedule from JSON</h3>
              <p style={{ fontSize: '0.8rem', color: 'var(--text-muted)', marginTop: '0.25rem' }}>
                Restore schedule from a previously exported JSON backup or external file.
              </p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
